
import { useState, useEffect, useMemo } from "react";
import { TripComponent } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface ExtractedComponentsReviewProps {
  components: TripComponent[];
  onImport: (selectedComponents: TripComponent[]) => void;
  isImporting: boolean;
}

const getComponentTypeColor = (type?: string | null): string => {
  const normalized = type?.toLowerCase() ?? "";

  switch (normalized) {
    case "flight":
      return "bg-blue-100 text-blue-800";
    case "hotel":
      return "bg-purple-100 text-purple-800";
    case "activity":
      return "bg-yellow-100 text-yellow-800";
    case "transport":
      return "bg-green-100 text-green-800";
    case "food":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getComponentIdentifier = (component: TripComponent): string => {
  if (component.id != null) {
    return String(component.id);
  }

  const parts = [
    component.title ?? "component",
    component.dayIndex != null ? String(component.dayIndex) : "0",
    component.startTime ?? "",
    component.endTime ?? "",
  ];

  return parts.join("|");
};

const formatDetails = (details: TripComponent["details"]): string | null => {
  if (details == null) {
    return null;
  }

  if (typeof details === "string") {
    return details;
  }

  if (typeof details === "number" || typeof details === "boolean") {
    return String(details);
  }

  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return String(details);
  }
};

export function ExtractedComponentsReview({
  components,
  onImport,
  isImporting
}: ExtractedComponentsReviewProps) {
  const [selectedComponents, setSelectedComponents] = useState<TripComponent[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  useEffect(() => {
    setSelectedComponents(components);
    setExpandedItems(components.slice(0, 3).map((component) => getComponentIdentifier(component)));
  }, [components]);

  const toggleComponent = (component: TripComponent) => {
    const identifier = getComponentIdentifier(component);
    setSelectedComponents((previous) => {
      const isSelected = previous.some((item) => getComponentIdentifier(item) === identifier);
      if (isSelected) {
        return previous.filter((item) => getComponentIdentifier(item) !== identifier);
      }
      return [...previous, component];
    });
  };

  const selectAll = () => {
    setSelectedComponents(components);
  };

  const deselectAll = () => {
    setSelectedComponents([]);
  };

  const componentsByDay = useMemo(() => {
    return components.reduce<Record<number, TripComponent[]>>((acc, component) => {
      const dayIndex = typeof component.dayIndex === "number" ? component.dayIndex : 0;
      if (!acc[dayIndex]) {
        acc[dayIndex] = [];
      }
      acc[dayIndex].push(component);
      return acc;
    }, {});
  }, [components]);

  const isComponentSelected = (component: TripComponent): boolean => {
    const identifier = getComponentIdentifier(component);
    return selectedComponents.some((item) => getComponentIdentifier(item) === identifier);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <span className="text-sm font-medium mr-2">
            {selectedComponents.length} of {components.length} selected
          </span>
          <Button variant="link" size="sm" onClick={selectAll} disabled={isImporting}>
            Select All
          </Button>
          <Button variant="link" size="sm" onClick={deselectAll} disabled={isImporting}>
            Deselect All
          </Button>
        </div>
        <Button
          onClick={() => onImport(selectedComponents)}
          disabled={selectedComponents.length === 0 || isImporting}
          className="flex items-center gap-2"
        >
          {isImporting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Importing...
            </>
          ) : (
            <>Import Selected</>
          )}
        </Button>
      </div>

      <div className="space-y-4">
        {Object.entries(componentsByDay)
          .sort(([dayA], [dayB]) => Number(dayA) - Number(dayB))
          .map(([day, dayComponents]) => (
            <div key={day} className="border rounded-md p-4">
              <h3 className="text-md font-medium mb-3">Day {Number(day) + 1}</h3>
              <Accordion
                type="multiple"
                value={expandedItems}
                onValueChange={setExpandedItems}
                className="space-y-2"
              >
                {dayComponents.map((component, index) => {
                  const identifier = getComponentIdentifier(component) || `${day}-${index}`;
                  const isSelected = isComponentSelected(component);
                  const formattedDetails = formatDetails(component.details);
                  const componentTitle = component.title ?? "Untitled component";

                  return (
                    <AccordionItem
                      key={identifier}
                      value={identifier}
                      className="border rounded-md overflow-hidden"
                    >
                      <div className="flex items-center px-4 py-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleComponent(component)}
                          className="mr-3"
                          disabled={isImporting}
                        />
                        <AccordionTrigger className="hover:no-underline flex-1">
                          <div className="flex items-center gap-2 text-left">
                            <Badge className={getComponentTypeColor(component.type)}>
                              {component.type ?? "Unknown"}
                            </Badge>
                            <span className="font-medium">{componentTitle}</span>
                            {component.startTime && (
                              <span className="text-sm text-gray-500 ml-auto">
                                {component.startTime}
                              </span>
                            )}
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="px-4 pb-3 pt-0">
                        <div className="ml-10 space-y-2 text-sm">
                          {component.location && (
                            <div>
                              <span className="font-medium">Location:</span> {component.location}
                            </div>
                          )}
                          {component.description && (
                            <div>
                              <span className="font-medium">Description:</span> {component.description}
                            </div>
                          )}
                          <div className="flex gap-4">
                            {component.startTime && (
                              <div>
                                <span className="font-medium">Start:</span> {component.startTime}
                              </div>
                            )}
                            {component.endTime && (
                              <div>
                                <span className="font-medium">End:</span> {component.endTime}
                              </div>
                            )}
                          </div>
                          {formattedDetails && (
                            <div>
                              <span className="font-medium">Details:</span>{" "}
                              <pre className="whitespace-pre-wrap font-sans text-sm">{formattedDetails}</pre>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          ))}
      </div>
    </div>
  );
}
